import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import MigrantQuestionnaire from './MigrantQuestionnaire';

/**
 * Route wrapper for the migrant questionnaire: resolves which student it is for,
 * shows a confirmation once submitted, and offers the filled official PDF.
 */
const MigrantQuestionnaireRoute = () => {
  const { studentIntakeId } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [done, setDone] = useState(null);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/migrant-questionnaire/status');
        const match = (res.data.students || []).find((s) => s.studentIntakeId === studentIntakeId);
        if (!cancelled) {
          if (!match) {
            setLoadError('That student could not be found on your account.');
          } else {
            setStudent(match);
            if (match.submissionId) setDone({ submissionId: match.submissionId, alreadyOnFile: true });
          }
        }
      } catch (e) {
        if (!cancelled) setLoadError('Could not load your students. Please try again.');
      }
    })();
    return () => { cancelled = true; };
  }, [studentIntakeId]);

  const viewPdf = async () => {
    try {
      const res = await axios.post(
        '/api/migrant-questionnaire/generate-pdf',
        { submissionId: done.submissionId },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      alert('Could not open the PDF: ' + (e.response?.data?.error || e.message));
    }
  };

  if (loadError) {
    return (
      <div className="add-district">
        <h2>Migrant Education Parent Questionnaire</h2>
        <div className="error-message">{loadError}</div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="add-district">
        <h2>Migrant Education Parent Questionnaire</h2>
        <div style={{
          background: '#d4edda', color: '#155724', border: '1px solid #c3e6cb',
          borderRadius: '4px', padding: '1rem', marginBottom: '1.5rem'
        }}>
          <strong>{done.alreadyOnFile ? 'Already on file' : 'Submitted — thank you'}</strong>
          {student?.studentName ? <> for {student.studentName}.</> : null}
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          <button type="button" className="btn-primary" onClick={viewPdf} style={{ width: 'fit-content' }}>
            View PDF
          </button>
          <button type="button" className="btn-secondary" onClick={() => navigate('/parent/forms')} style={{ width: 'fit-content' }}>
            Back to Forms
          </button>
        </div>
      </div>
    );
  }

  if (!student) return <div>Loading…</div>;

  return (
    <div className="add-district">
      <MigrantQuestionnaire
        studentIntakeId={studentIntakeId}
        studentName={student.studentName}
        // The status endpoint already returns grade, and the building is derived from it, so
        // passing it here lets the select pre-fill instead of asking a parent to answer a
        // question whose answer is never printed.
        studentGrade={student.grade}
        onComplete={(result) => setDone({ submissionId: result.submissionId })}
        onCancel={() => navigate('/parent/forms')}
      />
    </div>
  );
};

export default MigrantQuestionnaireRoute;
